import React, {useEffect} from 'react';
import {connect} from 'react-redux';
import styled, {css} from 'styled-components/native';
import {closeGlobalToast} from './actions';
import {RootState} from '../../redux/store';
import {ToastProps} from '../../interfaces/connected-components/toast';
import {isWeb} from '../../utils/responsive';
import TextGeneric from '../../components/textGeneric';
import {ToastType} from '../../utils/enums';
import Icon from '../../components/icon';
import {useTheme} from 'styled-components';
import {Theme} from '../../theme/lightTheme';
import {Gesture, GestureDetector} from 'react-native-gesture-handler';
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import {tr} from '../../translations';
import useResponsiveDimensions from '../../utils/hooks/useResponsiveDimensions';

const Container = styled(Animated.View)<{type: ToastType}>`
  border-radius: 20px;
  align-self: center;
  flex-direction: row;
  position: absolute;
  background-color: ${({theme, type}) =>
    theme.alert[`${type}BackgroundPrimary`] + (__DEV__ ? '' : 'DD')};
  z-index: 100000;
  min-height: ${({theme}) => theme.text.s1 * 1.8}px;
  ${isWeb
    ? css`
        box-shadow: 2px 4px 4px ${({theme}) => `${theme.text.dark}33`};
      `
    : css`
        shadow-color: ${({theme}) => theme.text.dark};
        shadow-opacity: 0.5;
        shadow-radius: 4px;
        shadow-offset: 2px 4px;
        elevation: 5;
      `}
`;
const LeftContainer = styled.View`
  width: ${({theme}) => theme.text.s1 * 1.5}px;
  height: 100%;
`;
const CloseIconContainer = styled.TouchableOpacity`
  width: ${({theme}) => theme.text.s3}px;
  height: ${({theme}) => theme.text.s3}px;
  align-items: flex-start;
  justify-content: flex-end;
`;
const IconContainer = styled.View`
  position: absolute;
  top: -${({theme}) => theme.text.s1 / 1.5}px;
  left: 10%;
`;
const BubbleContainer = styled.View`
  width: 100%;
  height: 100%;
  align-items: flex-start;
  justify-content: flex-end;
  overflow: hidden;
  border-radius: 20px;
`;
const TextContainer = styled.View`
  flex: 1;
  justify-content: center;
  padding: ${({theme}) => theme.text.s8 / 2}px;
`;
const Title = styled(TextGeneric)`
  color: ${({theme}) => theme.alert.textPrimary};
  font-size: ${({theme}) => theme.text.s8}px;
  padding-bottom: ${({theme}) => theme.text.s8 / 4}px;
  text-align: left;
`;
const Label = styled(TextGeneric)`
  color: ${({theme}) => theme.alert.textPrimary};
  font-size: ${({theme}) => theme.text.s9}px;
  text-align: left;
`;

/**
 * GlobalToast component that displays a toast message with animations and swipe gesture for dismissal.
 * Uses `react-native-reanimated` for smooth animations and `react-native-gesture-handler` for swipe gesture handling.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.toast - The toast object containing the message and visibility status.
 * @returns {React.Component} The animated GlobalToast component.
 */
const GlobalToast = ({toast}: ToastProps) => {
  const theme = useTheme() as Theme;
  const {
    title = '',
    message = '',
    type = ToastType.SUCCESS,
    isVisible = false,
  } = toast || {};
  const {wdp, getByScreenSize, isMobile} = useResponsiveDimensions();
  const width = wdp(100);
  const translateX = useSharedValue(-width);
  const opacity = useSharedValue(0);
  const scale = useSharedValue(0.8);

  useEffect(() => {
    let timeout: any = null;
    if (isVisible) {
      translateX.value = withSpring(0);
      opacity.value = withSpring(1);
      scale.value = withSpring(1);
      timeout = setTimeout(() => closeGlobalToast(), 4000);
    } else {
      translateX.value = withTiming(width, {}, () => {
        translateX.value = -width;
      });
      opacity.value = withTiming(0);
      scale.value = withTiming(0.8);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isVisible]);

  const panGesture = Gesture.Pan()
    .onUpdate(event => {
      translateX.value = event.translationX;
    })
    .onEnd(event => {
      if (Math.abs(event.translationX) > 150) {
        translateX.value = withTiming(
          500 * Math.sign(event.translationX),
          {},
          () => {
            runOnJS(closeGlobalToast)();
          },
        );
      } else {
        translateX.value = withSpring(0);
        scale.value = withSpring(1);
      }
    });

  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{translateX: translateX.value}, {scale: scale.value}],
      opacity: opacity.value,
    };
  });

  return (
    <GestureDetector gesture={panGesture}>
      <Container
        type={type}
        style={[
          {width: `${getByScreenSize(90, 40)}%`},
          isMobile ? {bottom: 30} : {top: 50, left: 20},
          animatedStyle,
        ]}>
        <LeftContainer>
          <IconContainer>
            <Icon
              name={`${type}Alert`}
              color={theme.alert[`${type}BackgroundSecondary`] + 'EE'}
              stroke={theme.alert.textPrimary + 'EE'}
              size={theme.text.s1}
            />
          </IconContainer>
          <BubbleContainer>
            <Icon
              name={`${type}Bubble`}
              color={theme.alert[`${type}BackgroundSecondary`] + 'EE'}
              size={theme.text.s1}
            />
          </BubbleContainer>
        </LeftContainer>
        <TextContainer>
          <Title bold>{title ? title : tr(`toast.${type}Title`)}</Title>
          <Label>{message}</Label>
        </TextContainer>
        <CloseIconContainer onPress={closeGlobalToast}>
          <Icon
            name={'x'}
            color={theme.alert.textPrimary + 'EE'}
            size={theme.text.s7}
          />
        </CloseIconContainer>
      </Container>
    </GestureDetector>
  );
};

/**
 * Maps the Redux store state to component props.
 * @param {Object} state - The Redux store state.
 * @returns {Object} The mapped component props.
 */
const mapStateToProps = (state: RootState) => ({
  toast: state.toast,
});

export default connect(mapStateToProps, null)(GlobalToast);
